import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-daisyui';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useAuthContext } from '@/context/AuthContext.tsx';
import supabaseClient from '@/api/supabaseClient.ts';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import { passwordSchema } from '$/utils/validators/passwordSchema.ts';

const INPUT_CLASS = '!bg-[#FAFAFA] !border !border-[rgba(204,204,204,0.5)] !rounded-xl !min-h-[48px] font-dm text-sm';

const emailSchema = z.object({
  email: z.string().min(1, { message: 'This field is required.' }).email('Invalid email'),
});

const passwordChangeSchema = z
  .object({
    password: passwordSchema,
    repeat_password: z.string().min(1, { message: 'This field is required.' }),
  })
  .superRefine(({ repeat_password, password }, ctx) => {
    if (repeat_password !== password) {
      ctx.addIssue({ code: 'custom', message: 'The passwords did not match', path: ['repeat_password'] });
    }
  });

type EmailSchema = z.infer<typeof emailSchema>;
type PasswordChangeSchema = z.infer<typeof passwordChangeSchema>;

const AccountPage: FC = () => {
  const { userProfile, session } = useAuthContext();
  const { t } = useTranslation();
  const currentEmail = session?.user?.email ?? userProfile?.email ?? '';
  const role = userProfile?.brand_role ?? userProfile?.role ?? '';

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center px-6 py-4 bg-[#f4f9ff] rounded-xl">
        <div className="flex flex-col gap-1">
          <h1 className="font-newsreader text-[28px] font-extrabold leading-[125%] text-[rgba(0,0,0,0.87)]">
            {t('accountPage.title')}
          </h1>
        </div>
      </div>
      <div className="flex flex-col gap-6 max-w-[368px]">
        <ProfileCard currentEmail={currentEmail} role={role} />
        <PasswordCard />
        <DataOwnershipCard />
      </div>
    </div>
  );
};

export default AccountPage;

function ProfileCard({ currentEmail, role }: { currentEmail: string; role: string }) {
  const [isEditing, setIsEditing] = useState(false);

  const {
    mutateAsync: updateEmail,
    isPending,
    isError,
    error,
    isSuccess,
    reset,
  } = useMutation({
    mutationFn: async (email: string) => {
      const { error } = await supabaseClient.auth.updateUser({ email });
      if (error) throw new Error(error.message);
    },
  });

  const {
    control,
    handleSubmit,
    reset: resetForm,
  } = useForm<EmailSchema>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: currentEmail },
  });

  const handleSave = (data: EmailSchema) => {
    void (async () => {
      try {
        await updateEmail(data.email);
        setIsEditing(false);
      } catch (e) {
        console.error(e);
      }
    })();
  };

  const handleEdit = () => {
    reset();
    resetForm({ email: currentEmail });
    setIsEditing(true);
  };

  return (
    <div className="bg-white rounded-[20px] border border-[rgba(204,204,204,0.5)] shadow-sm p-6 flex flex-col gap-5">
      <h2 className="font-dm font-semibold text-base text-[rgba(0,0,0,0.87)]">Profile</h2>

      {isSuccess && !isEditing && (
        <Alert status="success">Confirmation email sent. Please verify your new email address.</Alert>
      )}
      {isError && <Alert status="error">{(error as Error).message}</Alert>}

      {isEditing ? (
        <form onSubmit={handleSubmit(handleSave)} className="flex flex-col gap-4">
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                label="Email"
                error={errors.email?.message}
                value={value}
                onChange={onChange}
                placeholder="Email"
                labelClassName={INPUT_CLASS}
              />
            )}
          />
          <div className="flex gap-3">
            <LoadingButton
              loading={isPending}
              type="submit"
              className="flex-1 rounded-xl text-sm font-dm"
              style={{ backgroundColor: '#0F1B2A', color: '#fff', border: 'none' }}
            >
              Save
            </LoadingButton>
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="flex-1 h-12 rounded-xl text-sm font-dm border border-[rgba(204,204,204,0.5)] text-[rgba(0,0,0,0.6)] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="flex flex-col gap-1">
            <span className="font-dm text-xs text-[rgba(0,0,0,0.4)] uppercase tracking-wide">Email</span>
            <span className="font-dm text-sm text-[rgba(0,0,0,0.87)]">{currentEmail}</span>
          </div>
          {role && (
            <div className="flex flex-col gap-1">
              <span className="font-dm text-xs text-[rgba(0,0,0,0.4)] uppercase tracking-wide">Role</span>
              <span className="font-dm text-sm text-[rgba(0,0,0,0.87)]">{role}</span>
            </div>
          )}
          <button
            onClick={handleEdit}
            className="h-12 w-[216px] rounded-xl text-sm font-dm font-semibold text-white transition-colors hover:opacity-90"
            style={{ backgroundColor: '#0F1B2A' }}
          >
            Edit profile
          </button>
        </>
      )}
    </div>
  );
}

function DataOwnershipCard() {
  return (
    <div className="bg-[#F4F9FF] rounded-[20px] border border-[rgba(204,204,204,0.5)] p-6 flex flex-col gap-3">
      <h2 className="font-dm font-semibold text-base text-[rgba(0,0,0,0.87)]">Data Ownership</h2>
      <p className="font-dm text-sm text-[rgba(0,0,0,0.6)] leading-relaxed">
        You retain full ownership of all data entered into COMMAND. Your organization&apos;s data is never
        shared with third parties or used to train external models. You may request complete deletion
        of your data at any time by contacting{' '}
        <a href="mailto:privacy@zdecommand.com" className="font-semibold text-[#0F1B2A] underline">
          privacy@zdecommand.com
        </a>
        .
      </p>
    </div>
  );
}

function PasswordCard() {
  const [isEditing, setIsEditing] = useState(false);

  const {
    mutateAsync: updatePassword,
    isPending,
    isError,
    error,
    isSuccess,
    reset,
  } = useMutation({
    mutationFn: async (password: string) => {
      const { error } = await supabaseClient.auth.updateUser({ password });
      if (error) throw new Error(error.message);
    },
  });

  const {
    control,
    handleSubmit,
    reset: resetForm,
  } = useForm<PasswordChangeSchema>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: { password: '', repeat_password: '' },
  });

  const handleSave = (data: PasswordChangeSchema) => {
    void (async () => {
      try {
        await updatePassword(data.password);
        resetForm();
        setIsEditing(false);
      } catch (e) {
        console.error(e);
      }
    })();
  };

  const handleEdit = () => {
    reset();
    resetForm({ password: '', repeat_password: '' });
    setIsEditing(true);
  };

  return (
    <div className="bg-white rounded-[20px] border border-[rgba(204,204,204,0.5)] shadow-sm p-6 flex flex-col gap-5">
      <h2 className="font-dm font-semibold text-base text-[rgba(0,0,0,0.87)]">Password</h2>

      {isSuccess && !isEditing && <Alert status="success">Password updated successfully.</Alert>}
      {isError && <Alert status="error">{(error as Error).message}</Alert>}

      {isEditing ? (
        <form onSubmit={handleSubmit(handleSave)} className="flex flex-col gap-4">
          <Controller
            control={control}
            name="password"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                label="New password"
                error={errors.password?.message}
                value={value}
                onChange={onChange}
                type="password"
                placeholder="New password"
                labelClassName={INPUT_CLASS}
              />
            )}
          />
          <Controller
            control={control}
            name="repeat_password"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                label="Confirm password"
                error={errors.repeat_password?.message}
                value={value}
                onChange={onChange}
                type="password"
                placeholder="Confirm password"
                labelClassName={INPUT_CLASS}
              />
            )}
          />
          <div className="flex gap-3">
            <LoadingButton
              loading={isPending}
              type="submit"
              className="flex-1 rounded-xl text-sm font-dm"
              style={{ backgroundColor: '#0F1B2A', color: '#fff', border: 'none' }}
            >
              Save
            </LoadingButton>
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="flex-1 h-12 rounded-xl text-sm font-dm border border-[rgba(204,204,204,0.5)] text-[rgba(0,0,0,0.6)] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="flex flex-col gap-1">
            <span className="font-dm text-xs text-[rgba(0,0,0,0.4)] uppercase tracking-wide">Password</span>
            <span className="font-dm text-sm text-[rgba(0,0,0,0.87)]">••••••••</span>
          </div>
          <button
            onClick={handleEdit}
            className="h-12 w-[216px] rounded-xl text-sm font-dm font-semibold text-white transition-colors hover:opacity-90"
            style={{ backgroundColor: '#0F1B2A' }}
          >
            Change password
          </button>
        </>
      )}
    </div>
  );
}
